// Chart Browser rank snapshots: localStorage-backed last-seen ranks per
// country + chart type. This is intentionally separate from app comparison
// snapshots so chart browsing never changes per-app comparison history.

const STORAGE_KEY = 'gar_chart_rank_snapshots';

/**
 * @typedef {'up'|'down'|'new'|'same'} ChartRankDeltaStatus
 *
 * @typedef {object} ChartRankDelta
 * @property {ChartRankDeltaStatus} status
 * @property {number} magnitude
 * @property {number|null} previousRank
 * @property {number} currentRank
 *
 * @typedef {object} ChartRankSnapshot
 * @property {string} snappedAt
 * @property {Record<string, number>} ranks
 *
 * @typedef {Record<string, ChartRankSnapshot>} ChartRankSnapshotStore
 */

function defaultStore() {
  return typeof globalThis.localStorage !== 'undefined' ? globalThis.localStorage : null;
}

/**
 * @param {Storage} [store]
 * @returns {ChartRankSnapshotStore}
 */
export function readChartRankSnapshotStore(store) {
  const s = store ?? defaultStore();
  if (!s) return {};
  try {
    const raw = s.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

/**
 * @param {ChartRankSnapshotStore} data
 * @param {Storage} [store]
 */
function writeChartRankSnapshotStore(data, store) {
  const s = store ?? defaultStore();
  if (!s) return;
  try {
    s.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Storage quota exceeded or unavailable: chart deltas are best-effort.
  }
}

/**
 * @param {string} country
 * @param {string} chartType
 * @returns {string}
 */
function snapshotKey(country, chartType) {
  return `${String(country).toLowerCase()}:${String(chartType)}`;
}

/**
 * @param {unknown} value
 * @returns {number|null}
 */
function rankOrNull(value) {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? value
    : null;
}

/**
 * @param {unknown} value
 * @returns {string|null}
 */
function idOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  return String(value);
}

/**
 * Pure diff of previous vs current chart entries.
 *
 * Lower numeric rank is better, so moving from #8 to #3 is `up` by 5. Entries
 * absent from the prior snapshot are `new`; unchanged ranks are `same`.
 *
 * @param {Array<{trackId?: string|number|null, rank?: number|null}>} previous
 * @param {Array<{trackId?: string|number|null, rank?: number|null}>} current
 * @returns {Map<string, ChartRankDelta>}
 */
export function diffChartRanks(previous, current) {
  const previousRanks = new Map();
  if (Array.isArray(previous)) {
    for (const entry of previous) {
      const id = idOrNull(entry?.trackId);
      const rank = rankOrNull(entry?.rank);
      if (id && rank !== null) previousRanks.set(id, rank);
    }
  }

  const deltas = new Map();
  if (!Array.isArray(current)) return deltas;

  for (const entry of current) {
    const id = idOrNull(entry?.trackId);
    const currentRank = rankOrNull(entry?.rank);
    if (!id || currentRank === null) continue;

    const previousRank = previousRanks.get(id) ?? null;
    if (previousRank === null) {
      deltas.set(id, { status: 'new', magnitude: 0, previousRank: null, currentRank });
      continue;
    }

    const movement = previousRank - currentRank;
    if (movement > 0) {
      deltas.set(id, { status: 'up', magnitude: movement, previousRank, currentRank });
    } else if (movement < 0) {
      deltas.set(id, { status: 'down', magnitude: Math.abs(movement), previousRank, currentRank });
    } else {
      deltas.set(id, { status: 'same', magnitude: 0, previousRank, currentRank });
    }
  }

  return deltas;
}

/**
 * @param {string} country
 * @param {string} chartType
 * @param {Storage} [store]
 * @returns {ChartRankSnapshot|null}
 */
export function getChartRankSnapshot(country, chartType, store) {
  if (!country || !chartType) return null;
  const data = readChartRankSnapshotStore(store);
  const snapshot = data[snapshotKey(country, chartType)];
  if (!snapshot || typeof snapshot !== 'object') return null;
  if (!snapshot.ranks || typeof snapshot.ranks !== 'object' || Array.isArray(snapshot.ranks)) return null;
  return {
    snappedAt: typeof snapshot.snappedAt === 'string' ? snapshot.snappedAt : '',
    ranks: snapshot.ranks,
  };
}

/**
 * @param {string} country
 * @param {string} chartType
 * @param {Array<{trackId?: string|number|null, rank?: number|null}>} entries
 * @param {string} nowIso
 * @param {Storage} [store]
 */
export function saveChartRankSnapshot(country, chartType, entries, nowIso, store) {
  if (!country || !chartType || !Array.isArray(entries)) return;
  const data = readChartRankSnapshotStore(store);
  const ranks = {};
  for (const entry of entries) {
    const id = idOrNull(entry?.trackId);
    const rank = rankOrNull(entry?.rank);
    if (id && rank !== null) ranks[id] = rank;
  }
  data[snapshotKey(country, chartType)] = {
    snappedAt: nowIso,
    ranks,
  };
  writeChartRankSnapshotStore(data, store);
}

/**
 * @param {ChartRankSnapshot} snapshot
 * @returns {Array<{trackId: string, rank: number}>}
 */
function entriesFromSnapshot(snapshot) {
  return Object.entries(snapshot.ranks)
    .map(([trackId, rank]) => ({ trackId, rank }))
    .filter((entry) => rankOrNull(entry.rank) !== null);
}

/**
 * Read the previous chart snapshot for a country + chart type and diff it
 * against current entries. Returns an empty map when this is the first look.
 *
 * @param {string} country
 * @param {string} chartType
 * @param {Array<{trackId?: string|number|null, rank?: number|null}>} current
 * @param {Storage} [store]
 * @returns {{ deltas: Map<string, ChartRankDelta & { snappedAt: string }>, snappedAt: string|null }}
 */
export function getChartRankDeltas(country, chartType, current, store) {
  const snapshot = getChartRankSnapshot(country, chartType, store);
  if (!snapshot) return { deltas: new Map(), snappedAt: null };

  const rawDeltas = diffChartRanks(entriesFromSnapshot(snapshot), current);
  const deltas = new Map();
  for (const [trackId, delta] of rawDeltas) {
    deltas.set(trackId, { ...delta, snappedAt: snapshot.snappedAt });
  }
  return { deltas, snappedAt: snapshot.snappedAt };
}
